import {
  useDebounceCallback,
  useToast,
} from '@airship-platform/airship-patterns';
import { PriceSection } from '@airship-platform/airship-proposal';
import {
  deriveItemGroups,
  isItemGroupChild,
} from '@airship-platform/appointments-feature/utils';
import { FlexBox } from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import {
  AppointmentProposalOptionProduct,
  useAppointmentProposalOptionProducts,
} from '../../../data-access/appointmentProposalOptionProducts';
import {
  RemoveProductModal,
  useProductNavigationActions,
} from '../../proposal-option-items';
import { useItemGroupMutations } from '../hooks/useItemGroupMutations';
import SubItemRow from './SubItemRow';

export const ItemGroupFormSchema = z.object({
  itemGroupName: z
    .string()
    .min(1, 'Item group name is required')
    .max(250, 'Item group name must be less than 250 characters'),
});

interface SubItemsSectionProps {
  rootProduct: AppointmentProposalOptionProduct;
  isReadOnly: boolean;
}

const SubItemsSection: React.FC<SubItemsSectionProps> = ({
  rootProduct,
  isReadOnly,
}) => {
  const {
    updateAppointmentProposalOptionProduct,
    deleteAppointmentProposalOptionProduct,
    isLoading,
  } = useAppointmentProposalOptionProducts();
  const { getChildren } = useItemGroupMutations();
  const { navigateToQueryProducts } = useProductNavigationActions();
  const { showToast } = useToast();
  const { control } = useForm({
    defaultValues: {
      itemGroupName: rootProduct.itemGroupName || rootProduct.name,
    },
    resolver: zodResolver(ItemGroupFormSchema),
    mode: 'onChange',
  });
  const [childToRemove, setChildToRemove] =
    useState<AppointmentProposalOptionProduct | null>(null);

  const children = useMemo(
    () => getChildren(rootProduct.appointmentProposalOptionProductId),
    [getChildren, rootProduct.appointmentProposalOptionProductId]
  );

  const itemGroup = useMemo(
    () =>
      deriveItemGroups([rootProduct, ...children]).itemGroupsByRootId.get(
        rootProduct.appointmentProposalOptionProductId
      ),
    [rootProduct, children]
  );

  const debounceChange = useDebounceCallback(
    (
      appointmentProposalOptionProductId: string,
      update: Partial<
        Pick<AppointmentProposalOptionProduct, 'quantity' | 'itemGroupName'>
      >
    ) =>
      updateAppointmentProposalOptionProduct(
        rootProduct.appointmentId,
        rootProduct.appointmentProposalId,
        rootProduct.appointmentProposalOptionId,
        appointmentProposalOptionProductId,
        update
      ),
    1000
  );

  const groupingName = rootProduct.groupingName;
  if (isItemGroupChild(rootProduct) || groupingName === 'LINKED_ITEM') {
    return null;
  }

  const handleAddSubItems = (variant: 'sub-item' | 'material-sub-item') =>
    navigateToQueryProducts(
      variant === 'sub-item'
        ? {
            productTypes: ['EQUIPMENT', 'SERVICE'],
            groupingName,
            productPageTitle: 'Add sub-items',
            itemGroupRootId: rootProduct.appointmentProposalOptionProductId,
          }
        : {
            productTypes: ['MATERIAL'],
            groupingName,
            productPageTitle: 'Add material sub-items',
            itemGroupRootId: rootProduct.appointmentProposalOptionProductId,
          }
    );

  const handleDelete = (child: AppointmentProposalOptionProduct) =>
    deleteAppointmentProposalOptionProduct(
      child.appointmentId,
      child.appointmentProposalId,
      child.appointmentProposalOptionId,
      child.appointmentProposalOptionProductId
    ).then(() => {
      showToast('Sub-item removed', 'success');
      setChildToRemove(null);
    });

  return (
    <FlexBox flexDirection="column" gap={2}>
      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">
          Sub-items{children.length ? ` (${children.length})` : ''}
        </Typography>
        {children.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No sub-items have been added.
          </Typography>
        )}

        <FlexBox
          gap={1.5}
          justifyContent="flex-end"
          flexWrap="wrap"
          width={'100%'}
        >
          <Button
            color="softGold"
            startIcon={<PlusIcon />}
            disabled={isReadOnly}
            onClick={() => handleAddSubItems('sub-item')}
          >
            Sub-item
          </Button>
          <Button
            color="softGold"
            startIcon={<PlusIcon />}
            disabled={isReadOnly}
            onClick={() => handleAddSubItems('material-sub-item')}
          >
            Material sub-item
          </Button>
        </FlexBox>
      </FlexBox>

      {!!rootProduct.itemGroupName && (
        <FlexBox flexDirection="column">
          <Controller
            name={'itemGroupName'}
            control={control}
            disabled={isReadOnly}
            render={({ field, fieldState }) => (
              <TextField
                type="text"
                label={'Item group name'}
                {...field}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message || <>&nbsp;</>}
                onChange={(e) => {
                  const itemGroupName = e.target.value;
                  field.onChange(e);
                  if (itemGroupName) {
                    debounceChange(
                      rootProduct.appointmentProposalOptionProductId,
                      { itemGroupName }
                    );
                  }
                }}
              />
            )}
          />

          {children.map((child) => (
            <SubItemRow
              key={child.appointmentProposalOptionProductId}
              product={child}
              isReadOnly={isReadOnly || isLoading}
              onProductUpdate={(update) =>
                debounceChange(child.appointmentProposalOptionProductId, update)
              }
              onDelete={setChildToRemove}
            />
          ))}
          {itemGroup?.total !== undefined && (
            <FlexBox
              gap={2}
              flex={1}
              alignItems="center"
              justifyContent="flex-end"
              m={1}
            >
              <Typography variant="body1">Group subtotal:</Typography>
              <FlexBox
                gap={[0, 0, 2]}
                flex={1}
                flexDirection={['column', 'column', 'row']}
                justifyContent={['flex-start', 'flex-start', 'flex-end']}
                alignItems={['flex-end', 'flex-end', 'center']}
              >
                <PriceSection
                  value={itemGroup.total}
                  currency={'USD'}
                  quantity={1}
                  prefix=""
                  hasDiscount={!!itemGroup.hasMembership}
                  valueWithDiscount={itemGroup.membershipTotal}
                />
              </FlexBox>
            </FlexBox>
          )}
        </FlexBox>
      )}

      {childToRemove && (
        <RemoveProductModal
          product={childToRemove}
          isModalOpen
          isLoading={isLoading}
          onModalClose={() => setChildToRemove(null)}
          onProductRemove={() => handleDelete(childToRemove)}
        />
      )}
    </FlexBox>
  );
};

export default SubItemsSection;
